import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { isAutoCombinedLine } from './draftCart.thunks';

const selectDraftCartState = (state: RootState) => state.draftCart;

const selectDraftCartItems = createSelector(
  selectDraftCartState,
  (draftCart) => draftCart.cartItems
);

const selectDraftCartItemsQuantity = createSelector(
  selectDraftCartState,
  (draftCart) => draftCart.cartItemsQuantity
);

const selectDraftCartItemCollapseStates = createSelector(
  selectDraftCartState,
  (draftCart) => draftCart.cartItemCollapseStates
);

/**
 * HITL-1611 — summed quantities, not a key count.
 *
 * C3's My Cart header band shows how many items are in the cart, and two of the
 * same burger is two items, not one line. `AgentCartCard` counts the same way.
 */
const selectDraftCartItemCount = createSelector(
  selectDraftCartItemsQuantity,
  (quantities) =>
    Object.values(quantities ?? {}).reduce(
      (total, quantity) => total + (quantity || 0),
      0
    )
);

/**
 * Read by C3 (collapse the two-column split back to one) and C6 (enable
 * "Replace order with My Cart" only when there is something to promote).
 * Keyed on items rather than on the count, so a line with a zero quantity
 * still reads as present.
 */
const selectIsDraftCartEmpty = createSelector(
  selectDraftCartItems,
  (cartItems) => Object.keys(cartItems ?? {}).length === 0
);

/**
 * HITL-1649 — is there anything in the Live order worth copying?
 *
 * "Copy all" was gated only on My Cart being empty, so on a fresh task — no car,
 * no lines, nothing on either side — it sat there looking pressable and did
 * nothing when pressed. A control that answers a click with silence is worse
 * than one that says up front it cannot.
 *
 * Not `cartItems.length`: `copyOrderIntoDraftCart` SKIPS auto-combined lines,
 * whose `autoCombinedCartItemIds` address `cartSlice`'s own id space and mean
 * nothing in the draft. So the question is not "are there lines" but "are there
 * lines this copy would actually take" — an order of nothing but auto-combined
 * lines copies nothing, and must read as such. The predicate is shared with the
 * thunk (and with the per-item control's own gate) so the two cannot drift.
 */
const selectHasCopyableOrderLines = createSelector(
  (state: RootState) => state.cart.cartItems,
  (cartItems) =>
    Object.values(cartItems ?? {}).some(
      (cartItem) => !!cartItem && !isAutoCombinedLine(cartItem)
    )
);

export {
  selectDraftCartState,
  selectDraftCartItems,
  selectDraftCartItemsQuantity,
  selectDraftCartItemCollapseStates,
  selectDraftCartItemCount,
  selectIsDraftCartEmpty,
  selectHasCopyableOrderLines,
};
